import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getOfferState } from '../../../core/states/offer/offer.selector';
import { first, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { PersonState } from '../../../core/states/offer/offer.state';
import { DateService } from '../../../core/services/date.service';
import { isNotNullOrUndefined } from '../../../core/utils/typeUtils';
import {
  addPerson,
  removeOfferForPerson,
  updateOfferDetails,
  updatePerson,
} from '../../../core/states/offer/offer.actions';
import {
  CriticalKeyDataPerson,
  KeyDataChanges,
  KeyDataChangeType,
  KeyDataFormData,
  NewPerson,
  Person,
} from '../key-data.types';

@Injectable({
  providedIn: 'root',
})
export class KeyDataService {
  constructor(private store: Store) {}

  private static getPersonWithId(newPersons: CriticalKeyDataPerson[], id: number): CriticalKeyDataPerson | undefined {
    return newPersons.find((person) => person.personId === id);
  }

  private static getPersonIdsWhereGenderChanged(
    oldPersons: PersonState[],
    newPersons: CriticalKeyDataPerson[],
  ): number[] {
    return oldPersons
      .filter((person) => {
        const personWithId = KeyDataService.getPersonWithId(newPersons, person.personId);
        return personWithId && person.details?.genderOrPrenatal !== personWithId.gender;
      })
      .map((person) => person.personId);
  }

  private static isPerson(person: Person | NewPerson): person is Person {
    return 'personId' in person && isNotNullOrUndefined(person.personId);
  }

  private static getDateOfBirthFromPersonWithId(newPersons: CriticalKeyDataPerson[], id: number): string | undefined {
    const person = KeyDataService.getPersonWithId(newPersons, id);
    return person ? DateService.parseDateFromView(person.dateOfBirth) : undefined;
  }

  private static getPersonIdsWhereDateOfBirthChanged(
    oldPersons: PersonState[],
    newPersons: CriticalKeyDataPerson[],
  ): number[] {
    return oldPersons
      .filter((person) => {
        const dateOfBirth = KeyDataService.getDateOfBirthFromPersonWithId(newPersons, person.personId);
        return dateOfBirth && person.details?.dateOfBirth !== dateOfBirth;
      })
      .map((person) => person.personId);
  }

  private static hasSaleOfferBeginChanged(oldSaleOfferBegin: string | undefined, newSaleOfferBegin: string): boolean {
    return isNotNullOrUndefined(oldSaleOfferBegin) && oldSaleOfferBegin !== newSaleOfferBegin;
  }

  private static hasBfsNumberChanged(oldBfsNumber: number | undefined, newBfsNumber: number): boolean {
    return isNotNullOrUndefined(oldBfsNumber) && oldBfsNumber !== newBfsNumber;
  }

  public getKeyDataChangeType$(currentData: KeyDataFormData): Observable<KeyDataChangeType> {
    return this.getKeyDataChanges$(currentData).pipe(map((data) => data.changeType));
  }

  public saveFormData(keyDataForm: KeyDataFormData): void {
    const { persons, address, saleOfferBegin, existingCustomer } = keyDataForm;
    const { zipCode, bfsNumber, city } = address.value;

    this.getKeyDataChanges$(keyDataForm)
      .pipe(first())
      .subscribe((keyDataChanges) => {
        this.store.dispatch(
          updateOfferDetails({
            address: {
              zipCode,
              bfsNumber,
              city,
            },
            saleOfferBegin,
            existingCustomer,
          }),
        );

        persons.forEach((formPerson) => {
          if (KeyDataService.isPerson(formPerson)) {
            const removeOfferOfPerson = keyDataChanges.personIds.some((personId) => personId === formPerson.personId);
            this.updatePersonDataInStore(formPerson, removeOfferOfPerson);
          } else {
            this.addNewPersonDataToStore(formPerson);
          }
        });
      });
  }

  private addNewPersonDataToStore(formPerson: NewPerson): void {
    this.store.dispatch(
      addPerson({
        details: {
          firstName: formPerson.firstName || undefined,
          dateOfBirth: DateService.parseDateFromView(formPerson.dateOfBirth),
          genderOrPrenatal: formPerson.gender,
        },
      }),
    );
  }

  private updatePersonDataInStore(formPerson: Person, removeOfferOfPerson: boolean): void {
    this.store.dispatch(
      updatePerson({
        personId: formPerson.personId,
        details: {
          firstName: formPerson.firstName || undefined,
          dateOfBirth: DateService.parseDateFromView(formPerson.dateOfBirth),
          genderOrPrenatal: formPerson.gender,
        },
      }),
    );

    if (removeOfferOfPerson) {
      this.store.dispatch(removeOfferForPerson({ personId: formPerson.personId }));
    }
  }

  private getKeyDataChanges$(newData: KeyDataFormData): Observable<KeyDataChanges> {
    return this.store.select(getOfferState).pipe(
      first(),
      map(({ persons, address, saleOfferBegin }) => {
        if (
          KeyDataService.hasBfsNumberChanged(address?.bfsNumber, newData.address.value.bfsNumber) ||
          KeyDataService.hasSaleOfferBeginChanged(saleOfferBegin, newData.saleOfferBegin)
        ) {
          return {
            personIds: newData.persons.filter(KeyDataService.isPerson).map((person) => person.personId as number),
            changeType: 'BFS_OR_SALE_OFFER_BEGIN_CHANGED',
          };
        }

        const personIdsWithChanges = [
          ...KeyDataService.getPersonIdsWhereGenderChanged(persons, newData.persons.filter(KeyDataService.isPerson)),
          ...KeyDataService.getPersonIdsWhereDateOfBirthChanged(
            persons,
            newData.persons.filter(KeyDataService.isPerson),
          ),
        ];

        return {
          personIds: personIdsWithChanges,
          changeType: personIdsWithChanges.length === 0 ? 'NOTHING_CHANGED' : 'DATE_OF_BIRTH_OR_GENDER_CHANGED',
        };
      }),
    );
  }
}
